import React, { useRef, useState } from 'react';
import { useGetOne, useNotify } from 'react-admin';
import { Loader2, Mail, Send, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { Funeral } from '../../types';
import { apiFetchResponse } from '../../lib/apiFetch';
import { confirmWorkItem } from '../../funerals/workItemsApi';
import { PartnerDocsDialog } from './PartnerDocsDialog';
import {
  outstandingLine,
  PartnerDocItem,
  promptable,
} from './partnerDocRequirements';
import {
  announcePartnerDocsChanged,
  fetchPartnerDocsStatus,
} from './partnerDocsApi';
import { usePartnerDocsStatus } from './PartnerDocsPanel';

/**
 * §6 action buttons on a partner_documents work item: one per outstanding item
 * (Send… / Request… / Upload…), built from the case's CURRENT status so an item
 * handled elsewhere disappears. When the last one is handled, the work item is
 * confirmed optimistically (reconcile() would close it on its next sweep).
 */
export const PartnerDocsWorkItemActions: React.FC<{
  caseId: number | string;
  workItemId: string;
  onResolved?: () => void;
}> = ({ caseId, workItemId, onResolved }) => {
  const notify = useNotify();
  const { data: funeral } = useGetOne<Funeral>('funerals', { id: caseId });
  const { status } = usePartnerDocsStatus(caseId);
  const [dialog, setDialog] = useState<{
    mode: 'send' | 'request';
    item: PartnerDocItem;
  } | null>(null);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const closeIfDone = async () => {
    try {
      const fresh = await fetchPartnerDocsStatus(caseId);
      if (promptable(fresh).length === 0) {
        await confirmWorkItem(workItemId);
        onResolved?.();
      }
    } catch {
      // reconcile() closes it on its next sweep anyway
    }
  };

  const upload = async (file: File | undefined) => {
    if (!file || !uploadFor) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', uploadFor);
      const res = await apiFetchResponse(`documents/funeral/${caseId}/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok)
        throw new Error(
          `Upload failed (${res.status}). Password-protected PDFs: use the Documents tab.`
        );
      notify('Document uploaded', { type: 'success' });
      announcePartnerDocsChanged(caseId);
      void closeIfDone();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Upload failed', {
        type: 'error',
      });
    } finally {
      setBusy(false);
      setUploadFor(null);
    }
  };

  if (!status || !funeral) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }
  const items = promptable(status);
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing outstanding any more.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((i) => (
        <div
          key={`${i.partnerId}-${i.documentType}`}
          className="flex items-center justify-between gap-2 text-sm"
        >
          <span>{outstandingLine(i)}</span>
          {i.state === 'ready_to_send' && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setDialog({ mode: 'send', item: i })}
            >
              <Send className="mr-1 h-3.5 w-3.5" /> Send…
            </Button>
          )}
          {i.state === 'awaiting_partner' && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setDialog({ mode: 'request', item: i })}
            >
              <Mail className="mr-1 h-3.5 w-3.5" /> Request…
            </Button>
          )}
          {i.state === 'need_document' && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setUploadFor(i.documentType);
                fileRef.current?.click();
              }}
            >
              <Upload className="mr-1 h-3.5 w-3.5" /> Upload…
            </Button>
          )}
        </div>
      ))}
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {dialog && (
        <PartnerDocsDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          funeral={funeral}
          mode={dialog.mode}
          items={status.items.filter(
            (x) => x.partnerId === dialog.item.partnerId
          )}
          documentTypes={[dialog.item.documentType]}
          onSent={() => void closeIfDone()}
        />
      )}
    </div>
  );
};
